import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { addEmployeeApi } from '@/lib/api'
import { BRAND_MINT } from '@/lib/utils'
import { UserPlusIcon, UsersIcon } from 'lucide-react'

export const Route = createFileRoute('/dashboard/add-employee')({
  component: () => {
    const navigate = useNavigate()
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [employeeId, setEmployeeId] = useState('')
    const [bankName, setBankName] = useState('')
    const [accountNumber, setAccountNumber] = useState('')
    const [swiftBic, setSwiftBic] = useState('')
    const [routingNumber, setRoutingNumber] = useState('')
    const [loading, setLoading] = useState(false)

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (!firstName || !lastName || !email) {
        toast.error('Please fill in all fields')
        return
      }

      setLoading(true)
      try {
        const res = await addEmployeeApi({
          first_name: firstName,
          last_name: lastName,
          email,
          role: 'employee',
          employee_identifier: employeeId.trim() || undefined,
          bank_name: bankName.trim() || undefined,
          account_number: accountNumber.trim() || undefined,
          swift_bic: swiftBic.trim() || undefined,
          routing_number: routingNumber.trim() || undefined,
        })
        toast.success('Employee added successfully')
        // Land on the new employee's detail page so the employer can
        // immediately record tax info (compliance path, §3.2) if desired.
        if (res.user_id) {
          navigate({
            to: '/dashboard/employees/$userId',
            params: { userId: String(res.user_id) },
          })
        } else {
          navigate({ to: '/dashboard' })
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred'
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    return (
      <div className="mx-auto max-w-2xl">
        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-semibold">Add new employee</h1>
              <p className="text-muted-foreground text-sm">
                Register a new employee to the payroll platform. A secure password will be generated and sent automatically.
              </p>
            </div>
          </div>
        </div>

        <form className="rounded-xl border border-border bg-card p-6" onSubmit={handleSubmit}>
          <div className="space-y-5">
            <div className="flex items-center gap-3 pb-2">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[var(--brand-mint)]/10 text-[var(--brand-mint)]">
                <UserPlusIcon className="size-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Employee details</p>
                <p className="text-xs text-muted-foreground">Enter the new employee's basic information.</p>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Jane"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Smith"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="empEmail">Email</Label>
              <Input
                id="empEmail"
                type="email"
                placeholder="jane@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="empEmployeeId">Employee ID</Label>
              <Input
                id="empEmployeeId"
                type="text"
                placeholder="e.g. EMP-0042"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              />
            </div>

            {/* ── Bank account details ── */}
            <div className="flex items-center gap-3 pb-2 pt-2">
              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/10 text-blue-600">
                <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 0 4.5 6h.75m12 0h.75a.75.75 0 0 0 .75-.75V4.5M3.75 9.75h.75m12 0h.75M3.75 15h.75m12 0h.75" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Bank account</p>
                <p className="text-xs text-muted-foreground">Optional. For cross-org payments. All fields are optional.</p>
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="bankName">Bank name</Label>
                <Input
                  id="bankName"
                  type="text"
                  placeholder="e.g. Chase"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="accountNumber">Account number</Label>
                <Input
                  id="accountNumber"
                  type="text"
                  placeholder="e.g. IBAN or local number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="swiftBic">SWIFT / BIC</Label>
                <Input
                  id="swiftBic"
                  type="text"
                  placeholder="e.g. BOFAUS3N"
                  value={swiftBic}
                  onChange={(e) => setSwiftBic(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="routingNumber">Routing number</Label>
                <Input
                  id="routingNumber"
                  type="text"
                  placeholder="e.g. 021000021"
                  value={routingNumber}
                  onChange={(e) => setRoutingNumber(e.target.value)}
                />
              </div>
            </div>

            <div className="rounded-lg bg-muted/50 px-4 py-3 text-xs text-muted-foreground">
              A secure password will be automatically generated and sent to the employee's email.
            </div>

            <Button className="w-full gap-1.5" type="submit" disabled={loading} size="lg">
              {loading ? (
                <>
                  <span className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Adding employee…
                </>
              ) : (
                <>
                  <UserPlusIcon className="size-4" />
                  Add employee
                </>
              )}
            </Button>
          </div>
        </form>

        <div className="mt-6 flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-3 text-xs text-muted-foreground">
          <UsersIcon className="mt-0.5 size-4 shrink-0 text-[var(--brand-mint)]" />
          <p>
            After adding, the employee will receive their login credentials via email.
            They can download the mobile app or access the web portal to create Funding Circle requests.
          </p>
        </div>
      </div>
    )
  },
})
